import {
  TicketStatus,
  TicketPriority,
  TicketSource,
  UserRole,
  getUserRoleFromString,
} from '@/types/api';
import type {
  Ticket,
  CreateTicketRequest,
  UpdateTicketRequest,
  TicketQueryParams,
  PaginatedResponse,
  ApiResponse,
  TicketMetrics,
  Conversation,
  ConversationThreadDto,
  CreateConversationRequest,
  UpdateConversationRequest,
  TicketUser,
  TicketCompany,
} from '@/types/api';
import { apiClient } from './api';
import { apiEndpoints } from '@/config/app';
import { normalizeApiTimestamp } from '@/utils/ticketTimestamp';

type BackendEnum = number | string;

interface BackendUserSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: BackendEnum;
  isActive: boolean;
  avatarUrl?: string | null;
}

interface BackendCompanySummary {
  id: string;
  name: string;
  domain?: string | null;
  isActive: boolean;
}

interface BackendTicketDto {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  status: BackendEnum;
  priority: BackendEnum;
  channel: BackendEnum;
  tags?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  firstResponseAt?: string | null;
  requesterId: string;
  requester?: BackendUserSummary | null;
  requesterCreatedAt?: string | null;
  requesterUpdatedAt?: string | null;
  assignedAgentId?: string | null;
  assignedAgent?: BackendUserSummary | null;
  assignedAgentCreatedAt?: string | null;
  assignedAgentUpdatedAt?: string | null;
  companyId?: string | null;
  company?: BackendCompanySummary | null;
  companyCreatedAt?: string | null;
  companyUpdatedAt?: string | null;
}

interface BackendTicketSummary {
  id: string;
  ticketNumber: string;
  subject: string;
  status: BackendEnum;
  priority: BackendEnum;
  channel: BackendEnum;
  createdAt: string;
  updatedAt?: string | null;
  requesterId: string;
  requesterName: string;
  requesterEmail: string;
  requesterCreatedAt?: string | null;
  requesterUpdatedAt?: string | null;
  assignedAgentId?: string | null;
  assignedAgentName?: string | null;
  assignedAgentCreatedAt?: string | null;
  assignedAgentUpdatedAt?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  companyCreatedAt?: string | null;
  companyUpdatedAt?: string | null;
  isEscalated?: boolean;
  slaBreached?: boolean;
}

interface BackendPagedResponse<T> {
  data: T[];
  totalItems: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export class TicketService {
  private mapStatusToBackend(status: TicketStatus | string | number): number {
    if (typeof status === 'number') return status;
    const str = String(status).toLowerCase().replace(/[\s_-]+/g, '');
    switch (str) {
      case 'new': return 0;
      case 'open':
      case 'inprogress': return 1;
      case 'pending': return 2;
      case 'resolved': return 3;
      case 'closed': return 4;
      case 'onhold': return 5;
      case 'cancelled':
      case 'canceled': return 6;
      default: return 0;
    }
  }

  private mapStatusFromBackend(status: number | string): TicketStatus {
    // Handle both numeric and string values from backend
    const numericStatus = typeof status === 'number' ? status : parseInt(String(status), 10);

    const map: Record<number, TicketStatus> = {
      0: TicketStatus.New,
      1: TicketStatus.Open,
      2: TicketStatus.Pending,
      3: TicketStatus.Resolved,
      4: TicketStatus.Closed,
      5: TicketStatus.OnHold,
      6: TicketStatus.Cancelled,
    };

    return map[numericStatus] ?? TicketStatus.New;
  }

  /**
   * Validate and format date string from backend
   * Returns null for null/undefined inputs to preserve optional timestamps
   */
  private validateDate(dateString: string | null | undefined): string | null {
    const normalized = normalizeApiTimestamp(dateString);
    if (!normalized) return null;
    const date = new Date(normalized);
    if (isNaN(date.getTime()) || date.getTime() === 0 || date.getFullYear() < 2000) {
      return null; // Return null for invalid dates instead of current time
    }
    
    return normalized;
  }

  private requireDate(dateString: string | null | undefined, field: string): string {
    const value = this.validateDate(dateString);
    if (!value) throw new Error(`Ticket API returned an invalid ${field}`);
    return value;
  }

  private errorStatus(error: unknown): number | undefined {
    if (!error || typeof error !== 'object' || !('statusCode' in error)) return undefined;
    return typeof error.statusCode === 'number' ? error.statusCode : undefined;
  }

  private toError(error: unknown, fallback: string): Error {
    if (error instanceof Error) return error;
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return new Error(error.message);
    }
    return new Error(fallback);
  }

  // Map backend numeric enums to frontend string enums
  private mapBackendStatusToFrontend(value: BackendEnum): TicketStatus {
    if (typeof value === 'string' && isNaN(Number(value))) {
      const s = value.toLowerCase().replace(/[\s_-]+/g, '');
      if (s === 'new') return TicketStatus.New;
      if (s === 'open' || s === 'inprogress') return TicketStatus.Open;
      if (s === 'pending') return TicketStatus.Pending;
      if (s === 'resolved') return TicketStatus.Resolved;
      if (s === 'closed') return TicketStatus.Closed;
      if (s === 'onhold') return TicketStatus.OnHold;
      if (s === 'cancelled' || s === 'canceled') return TicketStatus.Cancelled;
      return TicketStatus.Open;
    }
    const map: Record<number, TicketStatus> = {
      0: TicketStatus.New,
      1: TicketStatus.Open,
      2: TicketStatus.Pending,
      3: TicketStatus.Resolved,
      4: TicketStatus.Closed,
      5: TicketStatus.OnHold,
      6: TicketStatus.Cancelled,
    };
    return map[Number(value)] ?? TicketStatus.Open;
  }

  private mapBackendPriorityToFrontend(value: BackendEnum): TicketPriority {
    if (typeof value === 'string' && isNaN(Number(value))) {
      return Object.values(TicketPriority).find(priority => priority.toLowerCase() === value.toLowerCase()) ?? TicketPriority.Medium;
    }
    const map: Record<number, TicketPriority> = {
      0: TicketPriority.Low,
      1: TicketPriority.Medium,
      2: TicketPriority.High,
      3: TicketPriority.Urgent,
      4: TicketPriority.Critical,
    };
    return map[Number(value)] ?? TicketPriority.Medium;
  }

  /**
   * Convert backend ticket data to frontend format
   */
  private mapChannel(value: BackendEnum): Ticket['source'] {
    const channels: Record<number, Ticket['source']> = {
      0: TicketSource.WebForm,
      1: TicketSource.Email,
      2: TicketSource.Chat,
      3: TicketSource.Phone,
      4: TicketSource.API,
      5: TicketSource.Social,
    };
    if (typeof value === 'string' && isNaN(Number(value))) {
      return Object.values(TicketSource).find(source => source.toLowerCase() === value.toLowerCase()) ?? TicketSource.WebForm;
    }
    return channels[Number(value)] ?? TicketSource.WebForm;
  }

  private mapUser(user: BackendUserSummary | null | undefined, id: string, createdAt?: string | null, updatedAt?: string | null, fallbackRole: UserRole = UserRole.EndUser): TicketUser {
    return {
      id: user?.id ?? id,
      email: user?.email ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      fullName: user?.fullName ?? 'Unknown User',
      role: typeof user?.role === 'number'
        ? user.role
        : user?.role ? getUserRoleFromString(user.role) : fallbackRole,
      isActive: user?.isActive ?? true,
      profileImageUrl: user?.avatarUrl ?? undefined,
      createdAt: this.validateDate(createdAt),
      updatedAt: this.validateDate(updatedAt),
    };
  }

  private mapCompany(company: BackendCompanySummary | null | undefined, id: string | null | undefined, createdAt?: string | null, updatedAt?: string | null): TicketCompany | undefined {
    if (!company && !id) return undefined;
    return {
      id: company?.id ?? id ?? '',
      name: company?.name ?? '',
      domain: company?.domain ?? undefined,
      isActive: company?.isActive ?? true,
      createdAt: this.validateDate(createdAt),
      updatedAt: this.validateDate(updatedAt),
    };
  }

  private mapBackendTicketToFrontend(data: BackendTicketDto): Ticket {
    return {
      id: data.id,
      ticketNumber: data.ticketNumber,
      subject: data.subject,
      description: data.description ?? '',
      status: this.mapBackendStatusToFrontend(data.status),
      priority: this.mapBackendPriorityToFrontend(data.priority),
      source: this.mapChannel(data.channel),
      requesterId: data.requesterId,
      requester: this.mapUser(data.requester, data.requesterId, data.requesterCreatedAt, data.requesterUpdatedAt),
      assignedToId: data.assignedAgentId ?? undefined,
      assignedTo: data.assignedAgentId || data.assignedAgent
        ? this.mapUser(data.assignedAgent, data.assignedAgentId ?? '', data.assignedAgentCreatedAt, data.assignedAgentUpdatedAt, UserRole.Agent)
        : undefined,
      companyId: data.companyId ?? undefined,
      company: this.mapCompany(data.company, data.companyId, data.companyCreatedAt, data.companyUpdatedAt),
      tags: (data.tags ?? '').split(',').map(tag => tag.trim()).filter(Boolean),
      customFields: {},
      createdAt: this.requireDate(data.createdAt, 'createdAt'),
      updatedAt: this.validateDate(data.updatedAt),
      resolvedAt: this.validateDate(data.resolvedAt),
      closedAt: this.validateDate(data.closedAt),
      firstResponseAt: this.validateDate(data.firstResponseAt),
      conversations: [],
      attachments: [],
    };
  }

  private mapTicketResponse(response: ApiResponse<BackendTicketDto>): ApiResponse<Ticket> {
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Ticket API returned no ticket');
    }
    return { ...response, data: this.mapBackendTicketToFrontend(response.data) };
  }
  /**
   * Get paginated list of tickets with filtering and sorting
   */
  async getTickets(params: TicketQueryParams = {}): Promise<ApiResponse<PaginatedResponse<Ticket>>> {
    return this.getTicketsAtPath(apiEndpoints.tickets.list, params);
  }

  private async getTicketsAtPath(path: string, params: TicketQueryParams): Promise<ApiResponse<PaginatedResponse<Ticket>>> {
    const queryParams = new URLSearchParams();

    // Add pagination
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    // Add filters
    if (params.statuses) {
      params.statuses.filter(Boolean).forEach(status => queryParams.append('Statuses', status));
    }
    if (params.priorities) {
      params.priorities.filter(Boolean).forEach(priority => queryParams.append('Priorities', priority));
    }
    if (params.sources) {
      params.sources.filter(Boolean).forEach(source => {
        queryParams.append('Sources', source);
      });
    }
    if (params.assignedAgentIds) {
      params.assignedAgentIds.filter(Boolean).forEach(agentId => queryParams.append('AssignedAgentIds', agentId));
    }
    if (params.requesterIds) {
      params.requesterIds.filter(Boolean).forEach(requesterId => queryParams.append('RequesterIds', requesterId));
    }
    if (params.companyIds) {
      params.companyIds.filter(Boolean).forEach(companyId => queryParams.append('CompanyIds', companyId));
    }
    if (params.categories) {
      params.categories.filter(Boolean).forEach(category => queryParams.append('Categories', category));
    }
    if (params.searchTerm) queryParams.append('SearchTerm', params.searchTerm);
    if (params.tags) {
      params.tags.filter(Boolean).forEach(tag => queryParams.append('Tags', tag));
    }

    // Add sorting
    if (params.sortBy) queryParams.append('SortBy', params.sortBy);
    if (params.sortDirection) queryParams.append('SortDirection', params.sortDirection);

    // Add date filters
    if (params.createdAfter) queryParams.append('CreatedAfter', params.createdAfter);
    if (params.createdBefore) queryParams.append('CreatedBefore', params.createdBefore);
    if (params.updatedAfter) queryParams.append('UpdatedAfter', params.updatedAfter);
    if (params.updatedBefore) queryParams.append('UpdatedBefore', params.updatedBefore);
    if (params.dueBefore) queryParams.append('DueBefore', params.dueBefore);
    if (params.isEscalated !== undefined) queryParams.append('IsEscalated', String(params.isEscalated));
    if (params.slaBreached !== undefined) queryParams.append('SLABreached', String(params.slaBreached));
    if (params.hasAttachments !== undefined) queryParams.append('HasAttachments', String(params.hasAttachments));
    if (params.onlyUnassigned !== undefined) queryParams.append('OnlyUnassigned', String(params.onlyUnassigned));
    if (params.onlyMyTickets !== undefined) queryParams.append('OnlyMyTickets', String(params.onlyMyTickets));

    const response = await apiClient.get<BackendPagedResponse<BackendTicketSummary>>(`${path}?${queryParams.toString()}`)
      .catch(error => { throw this.toError(error, 'Failed to fetch tickets'); });
    const pagedData = response.data;
    if (!response.success || !pagedData || !Array.isArray(pagedData.data)) {
      throw new Error(response.message || 'Ticket API returned an invalid page');
    }

    const transformedTickets: Ticket[] = pagedData.data.map(backendTicket => ({
      id: backendTicket.id,
      ticketNumber: backendTicket.ticketNumber,
      subject: backendTicket.subject,
      description: '',
      status: this.mapBackendStatusToFrontend(backendTicket.status),
      priority: this.mapBackendPriorityToFrontend(backendTicket.priority),
      source: this.mapChannel(backendTicket.channel),
      requesterId: backendTicket.requesterId,
      requester: {
        id: backendTicket.requesterId,
        email: backendTicket.requesterEmail,
        firstName: '',
        lastName: '',
        fullName: backendTicket.requesterName,
        role: UserRole.EndUser,
        isActive: true,
        createdAt: this.validateDate(backendTicket.requesterCreatedAt),
        updatedAt: this.validateDate(backendTicket.requesterUpdatedAt),
      },
      assignedToId: backendTicket.assignedAgentId ?? undefined,
      assignedTo: backendTicket.assignedAgentId ? {
        id: backendTicket.assignedAgentId,
        email: '',
        firstName: '',
        lastName: '',
        fullName: backendTicket.assignedAgentName ?? '',
        role: UserRole.Agent,
        isActive: true,
        createdAt: this.validateDate(backendTicket.assignedAgentCreatedAt),
        updatedAt: this.validateDate(backendTicket.assignedAgentUpdatedAt),
      } : undefined,
      companyId: backendTicket.companyId ?? undefined,
      company: backendTicket.companyId ? this.mapCompany({
        id: backendTicket.companyId,
        name: backendTicket.companyName ?? '',
        isActive: true,
      }, backendTicket.companyId, backendTicket.companyCreatedAt, backendTicket.companyUpdatedAt) : undefined,
      tags: [],
      customFields: {},
      isEscalated: backendTicket.isEscalated ?? false,
      slaBreached: backendTicket.slaBreached ?? false,
      createdAt: this.requireDate(backendTicket.createdAt, 'createdAt'),
      updatedAt: this.validateDate(backendTicket.updatedAt),
      conversations: [],
      attachments: [],
    }));

    return {
      success: true,
      data: {
        data: transformedTickets,
        totalCount: pagedData.totalItems,
        pageNumber: pagedData.page,
        pageSize: pagedData.pageSize,
        totalPages: pagedData.totalPages,
        hasNextPage: pagedData.hasNext,
        hasPreviousPage: pagedData.hasPrevious,
      },
      message: 'Tickets retrieved successfully',
    };
  }

  /**
   * Get a specific ticket by ID (GUID)
   */
  async getTicket(ticketId: string): Promise<ApiResponse<Ticket>> {
    try {
      const response = await apiClient.get<BackendTicketDto>(apiEndpoints.tickets.get(ticketId));
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Ticket API returned no ticket');
      }
      return {
        success: true,
        data: this.mapBackendTicketToFrontend(response.data),
        message: 'Ticket retrieved successfully',
      };
    } catch (error) {
      if (this.errorStatus(error) === 404) {
        throw new Error('The ticket does not exist or you do not have permission to view it');
      }
      throw this.toError(error, 'Failed to load ticket');
    }
  }
  /**
   * Create a new ticket
   */
  async createTicket(ticketData: CreateTicketRequest): Promise<ApiResponse<Ticket>> {
    // Transform frontend data structure to match backend expectations
    const transformedData = {
      subject: ticketData.subject,
      description: ticketData.description,
      priority: this.mapPriorityToEnum(ticketData.priority || 'Medium'), // Convert priority string to enum value
      channel: this.mapTicketSourceToChannel(ticketData.source || 'Web'),
      tags: Array.isArray(ticketData.tags) ? ticketData.tags.join(', ') : ticketData.tags || '',
      requesterId: ticketData.requesterId || undefined,
      assignedAgentId: ticketData.assignedToId || undefined, // Map assignedToId to AssignedAgentId
      companyId: ticketData.companyId || undefined,
      category: null,
      subcategory: null,
      dueDate: null,
      estimatedMinutes: null,
      initialMessage: null
    };

    return this.mapTicketResponse(await apiClient.post<BackendTicketDto>(apiEndpoints.tickets.create, transformedData));
  }

  /**
   * Map frontend TicketSource enum to backend TicketChannel enum
   */
  private mapTicketSourceToChannel(source: string): number {
    switch (source) {
      case 'Web':
        return 0; // Web
      case 'Email':
        return 1; // Email
      case 'Chat':
        return 2; // Chat
      case 'Phone':
        return 3; // Phone
      case 'API':
        return 4; // API
      case 'Social':
        return 5; // Social
      default:
        return 0; // Default to Web
    }
  }

  /**
   * Map frontend status string/enum to backend TicketStatus enum integer
   */
  public mapStatusToEnum(status: string): number {
    return this.mapStatusToBackend(status);
  }

  /**
   * Map frontend priority string to backend TicketPriority enum value
   */
  public mapPriorityToEnum(priority: string): number {
    switch (priority) {
      case 'Low':
        return 0;
      case 'Medium':
        return 1;
      case 'High':
        return 2;
      case 'Urgent':
        return 3;
      case 'Critical':
        return 4;
      default:
        return 1; // Default to Medium
    }
  }

  /**
   * Get a specific ticket by ticket number (e.g., "TKT-202409-001")
   */
  async getTicketByNumber(ticketNumber: string): Promise<ApiResponse<Ticket>> {
    try {
      const response = await apiClient.get<BackendTicketDto>(`/tickets/number/${encodeURIComponent(ticketNumber)}`);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Ticket API returned no ticket');
      }
      return {
        success: true,
        data: this.mapBackendTicketToFrontend(response.data),
        message: 'Ticket retrieved successfully',
      };
    } catch (error) {
      throw this.toError(error, `Failed to fetch ticket ${ticketNumber}`);
    }
  }
  /**
   * Update an existing ticket
   */
  async updateTicket(ticketId: string, updates: UpdateTicketRequest): Promise<ApiResponse<Ticket>> {
    const payload = {
      subject: updates.subject,
      description: updates.description,
      status: updates.status === undefined ? undefined : this.mapStatusToBackend(updates.status),
      priority: updates.priority === undefined ? undefined : this.mapPriorityToEnum(updates.priority),
      assignedAgentId: updates.assignedToId,
      tags: Array.isArray(updates.tags) ? updates.tags.join(', ') : updates.tags,
    };
    return this.mapTicketResponse(await apiClient.put<BackendTicketDto>(apiEndpoints.tickets.update(ticketId), payload));
  }

  /**
   * Delete a ticket (archive)
   */
  async deleteTicket(ticketId: string): Promise<ApiResponse<void>> {
    return await apiClient.delete<void>(apiEndpoints.tickets.delete(ticketId));
  }

  /**
   * Perform bulk operation on tickets
   */
  private async bulkOperation(data: {
    ticketIds: string[];
    operation: 'status_change' | 'priority_change' | 'assign';
    data: { status?: number; priority?: number; agent_id?: string };
  }): Promise<ApiResponse<{ successCount: number; failureCount: number; failedTickets: Array<{ ticketId: string; error: string }>; message: string }>> {
    const response = await apiClient.post<{ successCount: number; failureCount: number; failedTickets: Array<{ ticketId: string; error: string }>; message: string }>('/tickets/bulk', data);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Bulk ticket operation failed');
    }
    const failedCount = Math.max(
      response.data.failureCount,
      response.data.failedTickets?.length ?? 0,
      data.ticketIds.length - response.data.successCount
    );
    if (failedCount > 0) {
      const reason = response.data.failedTickets?.[0]?.error;
      throw new Error(`${failedCount} of ${data.ticketIds.length} tickets could not be updated${reason ? `: ${reason}` : ''}`);
    }
    return response;
  }

  /**
   * Bulk update status
   */
  async bulkUpdateStatus(ticketIds: string[], status: TicketStatus | string) {
    return await this.bulkOperation({
      ticketIds,
      operation: 'status_change',
      data: { status: this.mapStatusToBackend(status) }
    });
  }

  /**
   * Bulk update priority
   */
  async bulkUpdatePriority(ticketIds: string[], priority: TicketPriority | string) {
    return await this.bulkOperation({
      ticketIds,
      operation: 'priority_change',
      data: { priority: this.mapPriorityToEnum(String(priority)) }
    });
  }

  /**
   * Bulk assign tickets
   */
  async bulkAssign(ticketIds: string[], assignedAgentId: string) {
    return await this.bulkOperation({
      ticketIds,
      operation: 'assign',
      data: { agent_id: assignedAgentId }
    });
  }

  /**
   * Get ticket conversations/thread
   */
  async getTicketConversations(ticketId: string): Promise<ApiResponse<ConversationThreadDto>> {
    return await apiClient.get<ConversationThreadDto>(apiEndpoints.tickets.conversations(ticketId));
  }

  /**
   * Add a conversation/reply to a ticket
   */
  async createConversation(conversationData: CreateConversationRequest): Promise<ApiResponse<Conversation>> {
    // Transform frontend fields to backend format
    const backendPayload = {
      TicketId: conversationData.ticketId,
      Content: conversationData.content,
      IsPublic: !conversationData.isInternal, // Backend expects isPublic (opposite of isInternal)
      Type: conversationData.conversationType,
      StatusChangeAfterSend: conversationData.statusChangeAfterSend
        ? this.mapStatusToBackend(conversationData.statusChangeAfterSend)
        : undefined,
      AttachmentIds: conversationData.attachmentIds,
    };
    
    return await apiClient.post<Conversation>(apiEndpoints.tickets.conversations(conversationData.ticketId), backendPayload);
  }

  /**
   * Retry one durable failed outbound email. The backend owns idempotency and
   * returns the existing retry result when the same action is submitted twice.
   */
  async retryOutboundEmail(emailMessageId: string): Promise<ApiResponse<unknown>> {
    return await apiClient.post<unknown>(apiEndpoints.email.retryOutbound(emailMessageId));
  }

  /**
   * Update a conversation
   */
  async updateConversation(conversationId: string, updates: UpdateConversationRequest): Promise<ApiResponse<Conversation>> {
    return await apiClient.put<Conversation>(apiEndpoints.conversations.update(conversationId), updates);
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<ApiResponse<void>> {
    return await apiClient.delete<void>(apiEndpoints.conversations.delete(conversationId));
  }

  /**
   * Upload attachment to ticket
   */
  async uploadAttachment(
    ticketId: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ticketId', ticketId);

    return await apiClient.upload(apiEndpoints.attachments.upload, file, onUploadProgress);
  }

  /**
   * Download attachment
   */
  async downloadAttachment(attachmentId: string, filename?: string): Promise<void> {
    return await apiClient.download(apiEndpoints.attachments.download(attachmentId), filename);
  }

  /**
   * Delete attachment
   */
  async deleteAttachment(attachmentId: string): Promise<ApiResponse<void>> {
    return await apiClient.delete<void>(apiEndpoints.attachments.delete(attachmentId));
  }

  /**
   * Get ticket metrics/statistics
   */
  async getTicketMetrics(): Promise<ApiResponse<TicketMetrics>> {
    return await apiClient.get<TicketMetrics>(apiEndpoints.dashboard.metrics);
  }

  /**
   * Get tickets requested by the current user
   */
  async getMyTickets(params: Partial<TicketQueryParams> = {}): Promise<ApiResponse<PaginatedResponse<Ticket>>> {
    return this.getTicketsAtPath('/tickets/my-tickets', params);
  }

  /**
   * Search tickets
   */
  async searchTickets(query: string, filters?: Partial<TicketQueryParams>): Promise<ApiResponse<PaginatedResponse<Ticket>>> {
    return await this.getTickets({
      searchTerm: query,
      ...filters,
    });
  }

  // Note: Ticket watchers functionality has been removed as it's not currently needed
  // If watchers functionality is required in the future, implement the backend endpoints first:
  // POST /api/v1/tickets/{id}/watchers - Add watcher
  // DELETE /api/v1/tickets/{id}/watchers/{userId} - Remove watcher  
  // GET /api/v1/tickets/{id}/watchers - Get watchers

  /**
   * Get adjacent tickets (next/previous) based on current ticket list context
   * Uses current user's default sort order and filters
   */
  async getAdjacentTickets(currentTicketId: string, params: TicketQueryParams = {}): Promise<ApiResponse<{ previous: Ticket | null; next: Ticket | null }>> {
    try {
      // The API caps each page at 100 tickets. Keep the list's filters and sort
      // while walking pages so navigation also works across page boundaries.
      let previous: Ticket | null = null;
      for (let page = 1; ; page += 1) {
        const response = await this.getTickets({ ...params, page, pageSize: 100 });
        const tickets = response.data.data;
        const currentIndex = tickets.findIndex(ticket => ticket.id === currentTicketId);

        if (currentIndex !== -1) {
          const next = currentIndex < tickets.length - 1
            ? tickets[currentIndex + 1]
            : response.data.hasNextPage
              ? (await this.getTickets({ ...params, page: page + 1, pageSize: 100 })).data.data[0] ?? null
              : null;
          return {
            success: true,
            data: { previous: currentIndex > 0 ? tickets[currentIndex - 1] : previous, next },
          };
        }

        previous = tickets[tickets.length - 1] ?? previous;
        if (!response.data.hasNextPage || tickets.length === 0) {
          return {
            success: true,
            data: { previous: null, next: null },
            message: 'Current ticket not found in list',
          };
        }
      }
    } catch (error) {
      console.error('Error getting adjacent tickets:', error);
      return {
        success: false,
        message: 'Failed to get adjacent tickets',
        data: { previous: null, next: null }
      };
    }
  }

  /**
   * Update ticket subject
   */
  async updateTicketSubject(ticketId: string, newSubject: string): Promise<ApiResponse<Ticket>> {
    return this.updateTicket(ticketId, { subject: newSubject.trim() });
  }

  /**
   * Assign ticket to agent
   */
  async assignTicket(
    ticketId: string,
    agentId: string | null,
    options?: { note?: string; notifyAgent?: boolean; notifyRequester?: boolean }
  ): Promise<ApiResponse<Ticket>> {
    const payload = {
      AssignedAgentId: agentId || null,
      Note: options?.note ?? null,
      NotifyAgent: options?.notifyAgent ?? false,
      NotifyRequester: options?.notifyRequester ?? false,
    };

    return this.mapTicketResponse(await apiClient.patch<BackendTicketDto>(apiEndpoints.tickets.assign(ticketId), payload));
  }

  /**
   * Update ticket status
   */
  async updateTicketStatus(ticketId: string, status: TicketStatus | string | number, note?: string, notifyRequester?: boolean): Promise<ApiResponse<Ticket>> {
    // Backend expects numeric enum values; map string enum to numeric
    const updateStatusDto = {
      Status: this.mapStatusToBackend(status),
      Note: note || null,
      NotifyRequester: notifyRequester || false
    };
    return this.mapTicketResponse(await apiClient.patch<BackendTicketDto>(apiEndpoints.tickets.updateStatus(ticketId), updateStatusDto));
  }

  /**
   * Update ticket priority
   */
  async updateTicketPriority(
    ticketId: string,
    priority: TicketPriority,
    note?: string,
    notifyRequester?: boolean,
    notifyAgent?: boolean
  ): Promise<ApiResponse<Ticket>> {
    const updatePriorityDto = {
      Priority: this.mapPriorityToEnum(String(priority)),
      Note: note ?? null,
      NotifyRequester: notifyRequester ?? false,
      NotifyAgent: notifyAgent ?? false,
    };

    return this.mapTicketResponse(await apiClient.patch<BackendTicketDto>(apiEndpoints.tickets.updatePriority(ticketId), updatePriorityDto));
  }

}

// Create and export singleton instance
export const ticketService = new TicketService();
